import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useState, useMemo } from 'react';
import styles from './FrequentUpdates.module.scss';
import { ProductDetails } from '../../api/contracts/JobProductsResponse';

interface ProductsListProps {
  products: ProductDetails[];
  isLoading: boolean;
  type: 'failed' | 'unavailable';
}

export const ProductsList = ({
  products,
  isLoading,
  type,
}: ProductsListProps) => {
  const [selectedRetailer, setSelectedRetailer] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const retailers = useMemo(() => {
    if (!products) return [];
    return Array.from(
      new Set(products.map((product) => product.retailer))
    ).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!selectedRetailer) return products;
    return products.filter((product) => product.retailer === selectedRetailer);
  }, [products, selectedRetailer]);

  const handleRetailerClick = (retailer: string) => {
    setSelectedRetailer((prev) => (prev === retailer ? null : retailer));
  };

  if (isLoading) {
    return <Typography variant="body2">Loading {type} products...</Typography>;
  }

  if (!products || products.length === 0) {
    return <Typography variant="body2">No {type} products found</Typography>;
  }

  return (
    <div className={styles[`${type}Products`]}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {filteredProducts.length} {type} products found
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {retailers.map((retailer) => (
          <Chip
            size="small"
            key={retailer}
            label={retailer}
            onClick={() => handleRetailerClick(retailer)}
            color={selectedRetailer === retailer ? 'primary' : 'default'}
            variant={selectedRetailer === retailer ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mt: 1,
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            padding: isMobile ? '8px 4px' : '16px',
          },
        }}
      >
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell width={isMobile ? '40px' : '60px'}></TableCell>
              <TableCell>UPC</TableCell>
              {!isMobile && <TableCell>Retailer</TableCell>}
              <TableCell>Available</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </a>
                </TableCell>
                <TableCell>
                  <div>{product.upc}</div>
                  <div className={styles.productId}>{product.productId}</div>
                  {isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {product.retailer}
                    </Typography>
                  )}
                </TableCell>
                {!isMobile && <TableCell>{product.retailer}</TableCell>}
                <TableCell>{product.available ? 'Yes' : 'No'}</TableCell>
                <TableCell sx={{ maxWidth: isMobile ? '150px' : 'none' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: isMobile ? 2 : 'none',
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {product.extractionNote}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
